import React, { useState } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider, IconButton,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar, Menu,
  MenuItem, Tooltip, useTheme, useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';

const drawerWidth = 260;

// Role 매핑 헬퍼 (드롭다운 프로필 표시용)
const ROLE_LABELS = {
  ROLE_ADMIN: '관리자',
  ROLE_TEACHER: '교원회원',
  ROLE_USER: '일반회원',
};

const MainLayout = ({ children, user, onLogout, currentTab, setCurrentTab }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [desktopOpen, setDesktopOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDesktopOpen(!desktopOpen);
    }
  };

  const handleProfileClick = (e) => setAnchorEl(e.currentTarget);
  const handleProfileClose = () => setAnchorEl(null);

  // 내 정보 확인 이동 핸들러
  const handleOpenProfile = () => {
    handleProfileClose();
    if (setCurrentTab) setCurrentTab('profile');
  };

  // 메뉴 리스트 구성 (ADMIN 권한 체크)
  const menuItems = [
    { id: 'dashboard', text: '대시보드', icon: <DashboardRoundedIcon /> },
    { id: 'notice', text: '협회 소식 & 공지', icon: <CampaignRoundedIcon /> },
    // ADMIN 권한을 가진 경우에만 회원 관리 메뉴 노출
    ...(user?.role === 'ROLE_ADMIN' 
      ? [{ id: 'admin-members', text: '회원 관리 (관리자)', icon: <PeopleAltRoundedIcon /> }] 
      : []),
    { id: 'neulsign', text: '늘수어 학습 시스템', icon: <MenuBookRoundedIcon />, external: 'https://neulsign.com' },
  ];

  // Drawer 내부 메뉴 리스트
  const drawerContent = (isTemporary = false) => (
    <Box sx={{ overflow: 'auto', py: 2, px: (!isTemporary && !desktopOpen) ? 1 : 1.5 }}>
      <List>
        {menuItems.map((item) => {
          const isSelected = currentTab === item.id;
          return (
            <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.5 }}>
              <ListItemButton
                component={item.external ? 'a' : 'button'}
                href={item.external || undefined}
                target={item.external ? '_blank' : undefined}
                onClick={() => {
                  if (!item.external && setCurrentTab) {
                    setCurrentTab(item.id);
                  }
                  if (isTemporary) setMobileOpen(false);
                }}
                selected={isSelected}
                sx={{
                  minHeight: 48,
                  justifyContent: (!isTemporary && !desktopOpen) ? 'center' : 'initial',
                  px: 2.5,
                  borderRadius: '10px',
                  '&.Mui-selected': {
                    bgcolor: '#eff6ff',
                    color: '#2563eb',
                    '& .MuiListItemIcon-root': { color: '#2563eb' },
                  },
                  '&:hover': {
                    bgcolor: isSelected ? '#eff6ff' : '#f1f5f9',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: (!isTemporary && !desktopOpen) ? 'auto' : 2,
                    justifyContent: 'center',
                    color: isSelected ? '#2563eb' : '#475569',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    opacity: (!isTemporary && !desktopOpen) ? 0 : 1,
                    '& .MuiTypography-root': {
                      fontSize: '0.92rem',
                      fontWeight: isSelected ? 700 : 600,
                      color: isSelected ? '#2563eb' : '#334155',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* 상단 헤더 */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #e2e8f0',
          color: '#1e293b',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleDrawerToggle} edge="start" sx={{ color: '#475569' }}>
              <MenuIcon />
            </IconButton>

            {/* 브랜드 영역 */}
            <Box 
              onClick={() => setCurrentTab && setCurrentTab('dashboard')} 
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                }}
              >
                K
              </Box>
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.5px',
                  color: '#0f172a',
                  fontSize: { xs: '1rem', sm: '1.15rem' }
                }}
              >
                한국수어교원협회 
                <Box component="span" sx={{ fontSize: '0.8rem', color: '#64748b', ml: 0.5, fontWeight: 500, display: { xs: 'none', sm: 'inline' } }}>
                  KSLTA
                </Box>
              </Typography>
            </Box>
          </Box>

          {/* 우측 프로필 영역 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="계정 설정">
              <IconButton onClick={handleProfileClick} sx={{ p: 0.5 }}>
                <Avatar sx={{ bgcolor: '#3b82f6', width: 36, height: 36, fontSize: '0.9rem', fontWeight: 600 }}>
                  {user?.name ? user.name.charAt(0) : '교'}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleProfileClose}
              PaperProps={{
                elevation: 3,
                sx: { borderRadius: '12px', mt: 1, minWidth: 180, border: '1px solid #f1f5f9' }
              }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{user?.name || '선생님'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {ROLE_LABELS[user?.role] || user?.role || '일반회원'}
                </Typography>
              </Box>
              <Divider />
              {/* 내 정보 확인 메뉴 추가 */}
              <MenuItem onClick={handleOpenProfile} sx={{ gap: 1, fontSize: '0.9rem', py: 1 }}>
                <PersonOutlineRoundedIcon fontSize="small" sx={{ color: '#475569' }} /> 내 정보 확인
              </MenuItem>
              <MenuItem onClick={onLogout} sx={{ color: '#ef4444', gap: 1, fontSize: '0.9rem', py: 1 }}>
                <LogoutRoundedIcon fontSize="small" /> 로그아웃
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 모바일 Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', bgcolor: '#ffffff' },
          }}
        >
          <Toolbar />
          {drawerContent(true)}
        </Drawer>
      ) : (
        /* PC Drawer */
        <Drawer
          variant="permanent"
          open={desktopOpen}
          sx={{
            width: desktopOpen ? drawerWidth : 72,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            '& .MuiDrawer-paper': {
              width: desktopOpen ? drawerWidth : 72,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
              bgcolor: '#ffffff',
              borderRight: '1px solid #e2e8f0',
              boxSizing: 'border-box',
            },
          }}
        >
          <Toolbar />
          {drawerContent(false)}
        </Drawer>
      )}

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, width: '100%', overflowX: 'hidden' }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;